import PaymentTerms from "@husky-hrc/Components/PaymentTerms";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import translate from "@insite/client-framework/Translate";
import { WidgetDefinition } from "@insite/client-framework/Types/ContentItemDefinitions";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const enum fields {
    items = "items",
    showPaymentTerms = "showPaymentTerms",
}

interface FreightModel {
    fields: {
        label: string;
        text: string;
    };
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.items]: FreightModel[];
        [fields.showPaymentTerms]: boolean;
    };
}

const mapStateToProps = (state: ApplicationState) => ({
    cart: getCurrentCartState(state).value,
    isLoading: state.data.carts.isLoading,
});

type Props = OwnProps & ReturnType<typeof mapStateToProps>;

export interface FreightInfoStyles {
    gridContainerStyles?: GridContainerProps;
    titleGridItemStyles?: GridItemProps;
    titleTextStyles?: TypographyPresentationProps;
    contentGridItemStyles?: GridItemProps;
}

export const freightInfoStyles: FreightInfoStyles = {
    gridContainerStyles: {
        css: css`
            margin-top: 23px;
            margin-bottom: 60px;
        `,
    },
    titleGridItemStyles: {
        width: 12,
        css: css`
            align-items: center;
            height: 53px;
        `,
    },
    titleTextStyles: {
        variant: "h6",
        css: css`
            margin-bottom: 0;
            font-weight: 900;
            line-height: 22px;
        `,
    },
    contentGridItemStyles: {
        width: 12,
        css: css`
            font-size: 14px;
            letter-spacing: 0.31px;
            font-weight: bold;
            line-height: 30px;
            color: ${getColor("text.main")};
            white-space: pre-wrap;
            padding: 0 15px;
        `,
    },
};

const defaultValue: FreightModel[] = [
    {
        fields: {
            label: "Hotrunner Freight",
            text: "8 day lead time",
        },
    },
    {
        fields: {
            label: "Controller Freight",
            text: "4 day lead time",
        },
    },
];

const styles = freightInfoStyles;

const FreightInfo: React.FC<Props> = ({ fields, cart, isLoading }) => {
    if (!cart || !cart.cartLines || cart.cartLines.length < 1 || isLoading[cart.id]) {
        return null;
    }
    return (
        <GridContainer {...styles.gridContainerStyles}>
            <GridItem {...styles.titleGridItemStyles}>
                <Typography {...styles.titleTextStyles}>{translate("Freight Information")}</Typography>
            </GridItem>

            {fields.showPaymentTerms && (
                <GridItem {...styles.contentGridItemStyles}>
                    <PaymentTerms
                        paymentTermsDescription={cart.properties.paymentTermsDescription}
                        isFreightWidget={true}
                    />
                </GridItem>
            )}

            {fields.items &&
                fields.items.map((freightItem, index) => (
                    <GridItem key={index} {...styles.contentGridItemStyles}>
                        <Typography>{`${freightItem.fields.label}:   `}</Typography>
                        <Typography>{freightItem.fields.text}</Typography>
                    </GridItem>
                ))}
        </GridContainer>
    );
};

const definition: WidgetDefinition = {
    displayName: "Freight Info",
    group: "Cart",
    fieldDefinitions: [
        {
            name: fields.items,
            editorTemplate: "ListField",
            getDisplay: (item: HasFields) => {
                return item.fields.label;
            },
            defaultValue,
            fieldType: "Translatable",
            fieldDefinitions: [
                {
                    name: "label",
                    editorTemplate: "TextField",
                    defaultValue: "",
                },
                {
                    name: "text",
                    editorTemplate: "TextField",
                    defaultValue: "",
                },
            ],
        },
        {
            name: fields.showPaymentTerms,
            displayName: "Show PaymentTerms",
            editorTemplate: "CheckboxField",
            defaultValue: true,
            fieldType: "General",
            sortOrder: 1,
        },
    ],
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(FreightInfo),
    definition,
};

export default widgetModule;
